<script lang="ts">
  import SolanaShell from "../../components/SolanaShell.svelte";
  import HistoricTradesView from "../../components/HistoricTradesView.svelte";

  import { updateMeta, injectJsonLd } from "../../seo/meta";
  import { ROUTE_SEO } from "../../seo/routes";
  import { ORGANIZATION_SCHEMA, combineSchemas, getBreadcrumbSchema } from "../../seo/schemas";
  updateMeta(ROUTE_SEO["/tools/solana/historic"]);
  injectJsonLd(combineSchemas(
    ORGANIZATION_SCHEMA,
    getBreadcrumbSchema([
      { name: "Home", url: "https://0xfire.com/" },
      { name: "Tools", url: "https://0xfire.com/tools" },
      { name: "Solana", url: "https://0xfire.com/tools/solana" },
      { name: "Historic Data", url: "https://0xfire.com/tools/solana/historic" },
    ]),
  ));
</script>

<SolanaShell activePage="historic">
  <!-- Title -->
  <div class="text-center mb-8">
    <h1 class="text-2xl font-bold text-white tracking-tight">
      Historic Trade Data
    </h1>
    <p class="text-sm text-gray-500 mt-1">Query trades by date range, view candles, and apply technical indicators</p>
  </div>

  <!-- Tool -->
  <div class="max-w-4xl mx-auto">
    <HistoricTradesView />
  </div>

  <!-- SEO Content -->
  <div class="max-w-2xl mx-auto mt-16">
    <div class="border-t border-gray-800 pt-10">
      <h2 class="text-lg font-bold text-white mb-4">Solana Historic Trade Data</h2>
      <p class="text-sm text-gray-500 leading-relaxed mb-4">
        <strong class="text-gray-300">Query historical trades</strong> on Solana by date range and pool address. The date picker converts to Solana slot ranges automatically. Results are rendered as OHLC candlestick charts with configurable intervals from 1 second to 1 hour.
      </p>
      <p class="text-sm text-gray-500 leading-relaxed mb-6">
        Apply <strong class="text-gray-300">technical indicators</strong> including SMA, EMA, RSI, MACD, and Bollinger Bands directly on the chart. Hover over any candle to inspect OHLC values, volume, and indicator readings.
      </p>

      <ol class="space-y-3 text-sm text-gray-500 mb-10">
        <li class="flex gap-3">
          <span class="text-sky-500 font-bold shrink-0">1.</span>
          <span><strong class="text-gray-300">Pick a date range</strong> — select start and end times. The tool converts dates to Solana slot numbers automatically.</span>
        </li>
        <li class="flex gap-3">
          <span class="text-sky-500 font-bold shrink-0">2.</span>
          <span><strong class="text-gray-300">Enter a pool address</strong> — paste any Solana DEX pool address (PumpSwap, Raydium, Meteora, etc.).</span>
        </li>
        <li class="flex gap-3">
          <span class="text-sky-500 font-bold shrink-0">3.</span>
          <span><strong class="text-gray-300">Fetch & analyze</strong> — view candlestick charts, toggle indicators, paginate through trades.</span>
        </li>
      </ol>
    </div>

    <div class="border-t border-gray-800 pt-10">
      <h2 class="text-lg font-bold text-white mb-6">Frequently Asked Questions</h2>
      <div class="space-y-6">
        <div>
          <h3 class="text-sm font-bold text-white mb-1">What data sources are supported?</h3>
          <p class="text-sm text-gray-500 leading-relaxed">All major Solana DEXs — PumpSwap, PumpFun, Raydium AMM, Raydium CPMM, Raydium CLMM, Raydium Launchpad, Meteora Dynamic, and Meteora Dynamic V2.</p>
        </div>
        <div>
          <h3 class="text-sm font-bold text-white mb-1">How far back can I query?</h3>
          <p class="text-sm text-gray-500 leading-relaxed">Up to ~67 minutes per query (10 million Solana slots). Data is fetched in 1,000-slot batches from oldest to newest.</p>
        </div>
        <div>
          <h3 class="text-sm font-bold text-white mb-1">What candle intervals are available?</h3>
          <p class="text-sm text-gray-500 leading-relaxed">1 second, 5 seconds, 15 seconds, 1 minute, 5 minutes, 15 minutes, and 1 hour. Changing the interval re-aggregates the same data without refetching.</p>
        </div>
        <div>
          <h3 class="text-sm font-bold text-white mb-1">Which indicators can I use?</h3>
          <p class="text-sm text-gray-500 leading-relaxed">SMA (Simple Moving Average), EMA (Exponential Moving Average), RSI (Relative Strength Index), MACD, and Bollinger Bands. Each with configurable periods.</p>
        </div>
        <div>
          <h3 class="text-sm font-bold text-white mb-1">Is this free?</h3>
          <p class="text-sm text-gray-500 leading-relaxed">Yes — the historic query tool uses the Sailfish demo API. No sign-up or API key required.</p>
        </div>
        <div>
          <h3 class="text-sm font-bold text-white mb-1">Can I also query graduation events?</h3>
          <p class="text-sm text-gray-500 leading-relaxed">Yes — switch to the Graduations tab to query PumpFun and Raydium Launchpad pool graduations by date range.</p>
        </div>
      </div>
    </div>
  </div>
</SolanaShell>
